<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 p-1">
      <h2 class="mb-0 main-title">Material Categories</h2>
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-md btn-primary fs-11 fw-normal text-white" @click="openCreateModal('createModal')">
          Add New Category
        </button>
        <button type="button" class="btn btn-md btn-outline-primary fs-11 fw-normal text-primary hover-white">
          <i class="fa fa-list fs-11 text-primary hover-white"></i> Categories List
        </button>
      </div>
    </div>
    <div class="filters-wrapper">
      <div class="filters-bar" id="filtersBar"></div>

      <button @click="resetAllFilters" class="btn btn-sm btn-outline-danger hover-white">
        <span class="material-symbols-outlined fs-6">undo</span>
        Reset
      </button>
    </div>

    <!-- Dropdown Template -->
    <div id="genericFilterDropdownTemplate" style="display:none;">
      <div class="generic-filter-dropdown">
        <div class="dropdown-header">
          <input type="text" class="filter-search-input" placeholder="Search...">
        </div>

        <div class="dropdown-body filter-options-container"></div>

        <div class="dropdown-footer">
          <button class="filter-clear-btn">Clear</button>
          <button class="filter-apply-btn">Apply</button>
        </div>
      </div>
    </div>
    <div class="card p-1 bg-white">
     <FormModal ref="createModal" title="Create New Category" id="create_category_form_modal">
    <CreateCategory 
      ref="CreateCategoryForm" 
      @created="onCreated" 
      @close="closeCreateModal('createModal')" 
    />
  </FormModal>

      <!-- Loading State -->
      <LoadingBar v-if="loading" message="Loading Categories..." />

      <!-- Categories Table -->
      <div v-else class="table-wrapper">
        <ListTable>

          <colgroup>
            <col style="width: 8%"> <!-- SR# -->
            <col style="width: 35%"> <!-- Name -->
            <col style="width: 22%"> <!-- Category ID -->
            <col style="width: 35%"> <!-- Description -->
          </colgroup>

          <thead>
            <tr>
              <th>SR#</th>

              <th @click="sortBy('name')" class="sortable">
                Name <i :class="getSortIcon('name')"></i>
              </th>

              <th @click="sortBy('external_id')" class="sortable">
                Category ID <i :class="getSortIcon('external_id')"></i>
              </th>

              <th @click="sortBy('description')" class="sortable">
                Description <i :class="getSortIcon('description')"></i>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(category, index) in sortedData" :key="category.external_id">
              <td>
                {{ index + 1 }}
                 </td>

              <td>
                <div class="mb-0">{{ category.name }}</div>
                <div class="mt-0">
                  <small class="text-muted">
                    #{{ category.external_id?.slice(0, 8) || category.id }}
                  </small>
                </div>
              </td>

              <td>
                <span class="badge bg-light text-dark">
                  {{ category.external_id?.slice(0, 8) || category.id }}
                </span>
              </td>

              <td>
                {{ category.description || '—' }}
              </td>
            </tr>

            <tr v-if="sortedData.length === 0">
              <td colspan="4" class="text-center py-4">
                <i class="fa fa-folder-open fa-2x text-muted mb-2 d-block"></i>
                No Categories Found
              </td>
            </tr>
          </tbody>

        </ListTable>
      </div>
      <!-- Pagination -->
      <Pagination :pagination="pagination" @page-change="changePage" />
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import CreateCategory from '@/views/Categories/CreateCategory.vue'
import ListTable from '@/components/utils/ListTable.vue'

export default {
  name: 'CategoryList',
  components: {
    Pagination,
    LoadingBar,
    FormModal,
    CreateCategory,
    ListTable
  },
  data() {
    return {
      categories: [],
      pagination: {
        total_rows: 0,
        per_page: 20,
        current_page: 1,
        total_pages: 1
      },
      loading: false,
      sortColumn: 'name',
      sortOrder: 'asc',

      filterManager: null,
      currentFilters: {}
    }
  },
  computed: {
    sortedData() {
      let result = [...this.categories];

      if (this.sortColumn) {
        result.sort((a, b) => {
          let valA = a[this.sortColumn];
          let valB = b[this.sortColumn];

          if (valA === undefined || valA === null) valA = '';
          if (valB === undefined || valB === null) valB = '';

          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();

          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return result;
    }
  },
  mounted() {
    this.fetchList()

    this.$nextTick(() => {
      setTimeout(() => {
        this.initFilters()
      }, 100)
    })
  },
  methods: {
    openCreateModal(ref) {
      this.$refs[ref].showModal()
    },

    closeCreateModal(ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].closeModal()
      }
    },

    async onCreated() {
      this.pagination.current_page = 1
      await this.fetchList()
      // Close modal after data is refreshed
      this.closeCreateModal('createModal')
    },
    async fetchList() {
      this.loading = true

      const result = await form_api_submit({
        url: 'material-categories/list',
        method: 'GET',
        data: {
          page: this.pagination.current_page,

          id: this.currentFilters.id || '',
          name: this.currentFilters.name || '',
          description: this.currentFilters.description || '',

          sort_column: this.sortColumn,
          sort_order: this.sortOrder,
        },
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })
      if (result.success) {
        this.categories = result.data.list
        this.pagination = result.data.pagination
      }

      this.loading = false
    },
    initFilters() {
      if (typeof $ === "undefined" || typeof GenericFilterManager === "undefined") return;

      this.filterManager = new GenericFilterManager({
        containerId: "filtersBar",
        filters: [
          {
            key: "id",
            label: "Category ID",
            type: "input",
            placeholder: "Filter by Category ID"
          },
          {
            key: "name",
            label: "Name",
            type: "input",
            placeholder: "Filter by Name"
          },
          {
            key: "description",
            label: "Description",
            type: "input",
            placeholder: "Filter by Description"
          }
        ],
        onFilterChange: (filters) => {
          this.applyFilters(filters);
        }
      });
    },

    applyFilters(activeFilters) {
      this.currentFilters = { ...activeFilters };
      this.pagination.current_page = 1;
      this.fetchList();
    },

    resetPagination() {
      this.pagination.current_page = 1;
      this.fetchList();
    },

    resetAllFilters() {
      if (this.filterManager) {
        this.filterManager.clearAllFilters();
      }

      this.currentFilters = {};
      this.sortColumn = 'name';
      this.sortOrder = 'asc';
      this.pagination.current_page = 1;

      this.fetchList();
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return;

      this.pagination.current_page = page;
      this.fetchList();
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'fas fa-sort text-muted'
      }
      return this.sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    },

    viewCategory(category) {
      console.log('View category:', category)
      if (this.$toast) {
        this.$toast.info(`Viewing category: ${category.name}`)
      }
    },

    editCategory(category) {
      console.log('Edit category:', category)
      if (this.$toast) {
        this.$toast.info(`Edit category: ${category.name}`)
      }
    },

    deleteCategory(category) {
      if (confirm(`Are you sure you want to delete category "${category.name}"?`)) {
        console.log('Delete category:', category)
        if (this.$toast) {
          this.$toast.success(`Category "${category.name}" deleted successfully`)
        }
      }
    }
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable:hover {
  background-color: #f8f9fa;
}

.badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Table styling */
.table-wrapper {
  overflow-x: auto;
}

:deep(table) {
  width: 100%;
  border-collapse: collapse;
}

:deep(th) {
  background-color: #f8f9fa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  border-bottom: 1px solid #dee2e6;
}

:deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

:deep(tr:hover) {
  background-color: #f8f9fa;
}

/* Dropdown styling */
.dropdown-menu {
  font-size: 12px;
  min-width: 140px;
}

.dropdown-item {
  font-size: 12px;
  padding: 6px 16px;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

/* Responsive */
@media (max-width: 768px) {

  :deep(th),
  :deep(td) {
    padding: 8px 12px;
  }
}
</style>